import { arrayOf, bool, number, shape, string } from 'prop-types';

/**
 * @typedef {Object} ShowMorePropTypes
 * @property {Object} label - Etiqueta del botón "Mostrar más".
 * @property {string} label.text - Texto de la etiqueta.
 * @property {string} target - URL de destino del botón "Mostrar más".
 */

/**
 * @typedef {Object} ProgressBarPropTypes
 * @property {string} accessibility_text - Texto de accesibilidad para la barra de progreso.
 * @property {number} percent - Porcentaje de progreso.
 */

/**
 * @typedef {Object} PillPropTypes
 * @property {string} accessibility_text - Texto de accesibilidad para la píldora.
 * @property {string} text - Texto de la píldora.
 * @property {boolean} [color] - Indica si la píldora tiene un color asociado.
 * @property {Array<Object>} values - Valores asociados a la píldora.
 * @property {string} values.id - Identificador del valor.
 */

/**
 * @typedef {Object} EpigraphPropTypes
 * @property {string} text - Texto del epígrafe.
 * @property {Array<Object>} values - Valores asociados al epígrafe.
 * @property {string} values.text - Texto del valor.
 */

/**
 * @typedef {Object} HeaderPropTypes
 * @property {boolean} devices - Indica si el encabezado es compatible con dispositivos móviles.
 * @property {EpigraphPropTypes} epigraph - Información del epígrafe del encabezado.
 * @property {string} picture - URL de la imagen del encabezado.
 * @property {Array<PillPropTypes>} pills - Lista de etiquetas (píldoras) del encabezado.
 * @property {ProgressBarPropTypes} progress_bar - Información de la barra de progreso del encabezado.
 * @property {ShowMorePropTypes} show_more - Configuración del botón "Mostrar más" en el encabezado.
 * @property {Object} title - Título del encabezado.
 * @property {string} title.text - Texto del título.
 * @property {Array<Object>} title.values - Valores asociados al título.
 * @property {string} title.values.text - Texto del valor.
 */

/**
 * @typedef {Object} FooterPropTypes
 * @property {ShowMorePropTypes} show_more - Configuración del botón "Mostrar más".
 */

/**
 * @typedef {Object} CardPropTypes
 * @property {Object} action - Acción asociada a la tarjeta.
 * @property {string} action.text - Texto de la acción.
 * @property {string} action.link - Enlace de la acción.
 * @property {Object} content - Contenido de la tarjeta.
 * @property {Object} content.discount - Información del descuento.
 * @property {string} content.discount.text - Texto del descuento.
 * @property {Object} content.item - Información del ítem.
 * @property {string} content.item.category_id - Identificador de categoría del ítem.
 * @property {Object} content.item.cpg - Información del CPG relacionado.
 * @property {string} content.item.id - Identificador del ítem.
 * @property {string} content.item.inventory_id - Identificador del inventario.
 * @property {Object} content.item.product - Información del producto.
 * @property {string} content.item.product.id - Identificador del producto.
 * @property {string} content.item.title - Título del ítem.
 * @property {Object} content.name - Información del nombre.
 * @property {string} content.name.text - Texto del nombre.
 * @property {string} content.permalink - Enlace directo al ítem.
 * @property {Array<PillPropTypes>} content.pills - Lista de píldoras de información adicional.
 * @property {Object} content.picture - Información de la imagen del ítem.
 * @property {string} content.picture.id - Identificador de la imagen.
 * @property {Object} content.price - Información del precio del ítem.
 * @property {number} content.price.value - Valor del precio.
 * @property {string} content.price.currency_symbol - Símbolo de la moneda.
 * @property {string} content.price.decimal_value - Valor decimal del precio.
 * @property {Object} content.reviews - Información de las reseñas del ítem.
 * @property {number} content.reviews.rating_average - Promedio de calificaciones.
 * @property {string} content.reviews.accessibility_text - Texto de accesibilidad para las reseñas.
 * @property {string} content.reviews.rating_average_formatted - Calificación promedio formateada.
 * @property {string} content.reviews.formatted_total - Total formateado de reseñas.
 * @property {Object} content.reviews.rating_stars - Detalles de las estrellas de evaluación.
 * @property {number} content.reviews.rating_stars.value - Valor de las estrellas.
 * @property {string} content.reviews.rating_stars.color - Color de las estrellas.
 * @property {number} content.reviews.rating_stars.size - Tamaño de las estrellas.
 * @property {Object} content.add_to_cart_item - Información del ítem para agregar al carrito.
 * @property {string} content.add_to_cart_item.id - Identificador del ítem de carrito.
 * @property {string} content.add_to_cart_item.category_id - Identificador de categoría.
 * @property {Array<string>} content.add_to_cart_item.tags - Lista de etiquetas.
 * @property {number} content.add_to_cart_item.available_quantity - Cantidad disponible.
 * @property {number} content.add_to_cart_item.minimum_quantity - Cantidad mínima que se puede comprar.
 * @property {string} content.add_to_cart_item.inventory_id - Identificador del inventario.
 * @property {number} content.add_to_cart_item.threshold - Umbral de cantidad.
 * @property {string} content.add_to_cart_item.group_by - Criterio para agrupar ítems.
 * @property {boolean} content.add_to_cart_item.bookmarked - Indica si está marcado.
 * @property {Object} content.add_to_cart_item.cpg - Información del CPG.
 * @property {string} content.add_to_cart_item.cpg.id - Identificador del CPG.
 * @property {string} content.add_to_cart_item.cpg.type - Tipo de CPG.
 * @property {Object} content.add_to_cart_item.cpg.action - Acción relacionada con el CPG.
 * @property {Object} content.add_to_cart_item.cpg.action.label - Etiqueta de acción.
 * @property {string} content.add_to_cart_item.cpg.action.label.text - Texto de la etiqueta.
 * @property {number} content.add_to_cart_item.cpg.quantity - Cantidad del CPG.
 * @property {Object} content.add_to_cart_item.cpg.label_count - Conteo de etiquetas.
 * @property {string} content.add_to_cart_item.cpg.label_count.text - Texto de conteo de etiquetas.
 * @property {Array<Object>} content.add_to_cart_item.cpg.label_count.values - Valores asociados al conteo.
 * @property {number} content.add_to_cart_item.cpg.label_count.quantity - Cantidad total de etiquetas.
 * @property {Object} content.add_to_cart_item.cpg.tracks - Rastreadores relacionados con el CPG.
 * @property {Object} content.add_to_cart_item.cpg.tracks.melidata_track - Rastreadores de Melidata.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.path - Ruta del rastreador.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.type - Tipo de rastreo.
 * @property {Object} content.add_to_cart_item.cpg.tracks.melidata_track.event_data - Datos del evento de rastreo.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.context - Contexto del evento.
 * @property {Array<Object>} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items - Ítems rastreados.
 * @property {Object} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.item - Información del ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.item.id - Identificador del ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.item.category_id - Identificador de categoría del ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.unit_price - Precio unitario.
 * @property {number} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.quantity - Cantidad del ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.items.currency_id - Identificador de la moneda.
 * @property {string} content.add_to_cart_item.cpg.tracks.melidata_track.event_data.flow_type - Tipo de flujo del evento.
 * @property {Object} content.add_to_cart_item.cpg.tracks.path - Rutas para acciones relacionadas.
 * @property {string} content.add_to_cart_item.cpg.tracks.path.remove - Ruta para eliminar el ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.path.add - Ruta para agregar el ítem.
 * @property {string} content.add_to_cart_item.cpg.tracks.path.error - Ruta para manejo de errores.
 * @property {number} content.add_to_cart_item.cart_request_initial_delay - Retraso inicial para solicitudes de carrito.
 * @property {number} content.add_to_cart_item.cart_request_delay - Retraso para solicitudes de carrito.
 * @property {boolean} content.add_to_cart_item.should_call_suggestions - Indica si se deben llamar sugerencias.
 * @property {Array<string>} content.add_to_cart_item.variations_info - Información sobre variaciones disponibles.
 * @property {Array<string>} content.add_to_cart_item.variation_ids - Identificadores de variaciones.
 * @property {boolean} content.add_to_cart_item.accepts_mercado_pago - Indica si acepta Mercado Pago.
 * @property {boolean} content.add_to_cart_item.has_color_variations - Indica si tiene variaciones de color.
 * @property {number} content.add_to_cart_item.cart_quantity - Cantidad en el carrito para este ítem.
 */

/**
 * @typedef {Object} DynamicAccessPropTypes
 * @property {Array<CardPropTypes>} cards - Lista de tarjetas a mostrar en el carrusel.
 */

/**
 * @typedef {Object} InterventionJsDocs
 * @property {string} classname - Clase CSS para el contenedor principal.
 * @property {DynamicAccessPropTypes} dynamic_access - Información dinámica de acceso.
 * @property {FooterPropTypes} footer - Información del pie de la intervención.
 * @property {HeaderPropTypes} header - Información del encabezado de la intervención.
 * @property {string} store_id - Identificador de la tienda asociada a la intervención.
 */

export const interventionPropTypes = {
  intervention: shape({
    classname: string,
    dynamic_access: shape({
      cards: arrayOf(
        shape({
          action: shape({
            text: string,
            link: string,
          }),
          content: shape({
            discount: shape({
              text: string,
            }),
            item: shape({
              category_id: string,
              cpg: shape({}),
              id: string,
              inventory_id: string,
              product: shape({
                id: string,
              }),
              title: string,
            }),
            name: shape({
              text: string,
            }),
            permalink: string,
            pills: arrayOf(
              shape({
                accessibility_text: string,
                text: string,
                values: arrayOf(
                  shape({
                    id: string,
                  }),
                ),
              }),
            ),
            picture: shape({
              id: string,
            }),
            price: shape({
              value: number,
              currency_symbol: string,
              decimal_value: string,
            }),
            reviews: shape({
              rating_average: number,
              accessibility_text: string,
              rating_average_formatted: string,
              formatted_total: string,
              rating_stars: shape({
                value: number,
                color: string,
                size: number,
              }),
            }),
            add_to_cart_item: shape({
              id: string,
              category_id: string,
              tags: arrayOf(string),
              available_quantity: number,
              minimum_quantity: number,
              inventory_id: string,
              threshold: number,
              group_by: string,
              bookmarked: bool,
              cpg: shape({
                id: string,
                type: string,
                action: shape({
                  label: shape({
                    text: string,
                  }),
                }),
                quantity: number,
                label_count: shape({
                  text: string,
                  values: arrayOf(
                    shape({
                      key: string,
                      type: string,
                      text: string,
                      color: string,
                      font_weight: string,
                    }),
                  ),
                  quantity: number,
                }),
                tracks: shape({
                  melidata_track: shape({
                    path: string,
                    type: string,
                    event_data: shape({
                      context: string,
                      items: arrayOf(
                        shape({
                          item: shape({
                            id: string,
                            category_id: string,
                          }),
                          unit_price: string,
                          quantity: number,
                          currency_id: string,
                        }),
                      ),
                      flow_type: string,
                    }),
                  }),
                  path: shape({
                    remove: string,
                    add: string,
                    error: string,
                  }),
                }),
                cart_request_initial_delay: number,
                cart_request_delay: number,
                should_call_suggestions: bool,
              }),
              variations_info: arrayOf(string),
              variation_ids: arrayOf(string),
              accepts_mercado_pago: bool,
              has_color_variations: bool,
              cart_quantity: number,
            }),
          }),
          id: string,
          type: string,
        }),
      ),
    }).isRequired,
    footer: shape({
      show_more: shape({
        label: shape({
          text: string,
        }),
        target: string,
        track: shape({
          event_data: shape({
            intervention_tracking_id: string,
            group_type: string,
            flow_type: string,
            got_free_shipping: bool,
            intervention_position: number,
            view_all_products_link: string,
          }),
          path: string,
        }),
      }),
    }).isRequired,
    store_id: string,
    header: shape({
      devices: bool,
      epigraph: shape({
        text: string,
        values: arrayOf(
          shape({
            text: string,
          }),
        ),
      }),
      picture: string,
      pills: arrayOf(
        shape({
          accessibility_text: string,
          text: string,
          color: bool,
          values: arrayOf(
            shape({
              id: string,
            }),
          ),
        }),
      ),
      progress_bar: shape({
        accessibility_text: string,
        percent: number,
      }),
      show_more: shape({
        label: shape({
          text: string,
        }),
        target: string,
        track: shape({
          event_data: shape({
            intervention_tracking_id: string,
            group_type: string,
            flow_type: string,
            got_free_shipping: bool,
            intervention_position: number,
            view_all_products_link: string,
          }),
          path: string,
        }),
      }),
      title: shape({
        mobile: bool,
        text: string,
        values: arrayOf(
          shape({
            text: string,
          }),
        ),
      }),
    }).isRequired,
  }),
  mobile: bool,
};
